import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Number3 } from '@fabric-web/3d-viewer';
import { DialogContent, IconButton, Box } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  StyledDialog,
  ImageNumbering,
  CurrentPoiName,
  DialogContentStyle,
  MainContentContainer,
  PoiNavigationContainer,
  ImgNavButton,
} from './styles';
import { abyssColors } from '@/theme/colors';
import { DialogHeader } from './DialogHeader';
import { DetailsSidebar } from './DetailsSidebar';
import {
  useGetStructureForInspectionDataLoaderQuery,
  GenerateImageInput,
  FileTypes,
  PointOfInterest,
  FileUploadStatus,
  ImageType,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useFoundViewpoints } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/hooks/useFoundViewpoints';
import {
  generatePoiDisplayName,
  mapAttachmentType,
} from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { thumbnailInputFromLookAt } from '@/components/Analysis/Viewer/Drawers/FindViewpointsTool/ViewpointThumbnail';
import { useGenerateImage } from '@/components/Analysis/modules/thumbnails';
import { useImageZoomAndDrag } from '@/hooks/useImageZoomAndDrag';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { MainImageSection } from './MainImageSection';
import { ThumbnailList } from './ThumbnailList';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { sortPoiAttachments } from '@/utils';
import { ImageWithId } from '@/types';
import { PointOfInterestCaption } from '@/components/Analysis/ReviewScreen/PointsOfInterest/PointOfInterestCaption/PointOfInterestCaption';
import { LocationTag } from '@/constants';

type ImagePreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  currentImageIndex: number;
  onChangeImage: (index: number) => void;
  poiIndex?: number;
};

enum NavigationDirection {
  Next = 'next',
  Previous = 'previous',
}

export const ImagePreviewDialog = ({
  open,
  onClose,
  currentImageIndex,
  onChangeImage,
  poiIndex,
}: ImagePreviewDialogProps) => {
  const [viewpointImages, setViewpointImages] = useState<ImageWithId[]>([]);
  const [mainImageLoading, setMainImageLoading] = useState(false);
  const setInspectionMetadata = useSetAtom(state.inspectionMetadata);
  const [currentPoiIndex, setCurrentPoiIndex] = useState<number | undefined>(poiIndex);
  const allPois = useAtomValue(poiState.structurePois);
  const structureId = useGetStructureId();
  const setCurrentPoi = useSetAtom(poiState.selectedPointOfInterest);
  const currentPoi = allPois[currentPoiIndex!] as PointOfInterest;
  setCurrentPoi(currentPoi);
  const poiName = generatePoiDisplayName({
    name: currentPoi?.name,
    poiId: currentPoi?.poiId,
    poiType: currentPoi?.type,
  });

  const { imageTransform, imageEventHandlers, handleZoom, resetImageState, canZoomIn, canZoomOut } =
    useImageZoomAndDrag({
      maxScale: 3,
      minScale: 1,
      scaleStep: 0.5,
      xLimitMultiplier: 200,
      yLimitMultiplier: 150,
    });

  const { data: structureData } = useGetStructureForInspectionDataLoaderQuery({
    variables: { structureId },
    fetchPolicy: 'cache-first',
    skip: !structureId,
  });

  const poiCenterPoint = useMemo(() => {
    const { x, y, z } = currentPoi?.centerPoint3d ?? {};
    const centerPoint3dExists = x !== undefined && y !== undefined && z !== undefined;
    return centerPoint3dExists ? ([x, y, z] as Number3) : undefined;
  }, [currentPoi?.centerPoint3d]);

  const { foundViewpoints } = useFoundViewpoints(poiCenterPoint);
  const { fetchCropFromSpherical } = useGenerateImage();

  const fileData = useMemo(() => {
    return (
      currentPoi?.files
        ?.filter(
          (file) =>
            (file.type === FileTypes.Png || file.type === FileTypes.Jpeg) &&
            file.uploadStatus === FileUploadStatus.Success &&
            file.imageType !== ImageType.WhiteBgPdf
        )
        .map((file) => ({
          id: file.id,
          uuid: file.uuid,
          path: file.resourcePath,
          name: file.name || 'Attachment',
          caption: file.caption ?? undefined,
          imageType: file.imageType,
          fileName: file.fileName,
        })) || []
    );
  }, [currentPoi?.files]);

  useEffect(() => {
    if (structureData?.structure) {
      setInspectionMetadata(structureData.structure);
    }
  }, [structureData?.structure, setInspectionMetadata]);

  useEffect(() => {
    const fetchViewpointImages = async () => {
      if (!foundViewpoints?.length) {
        setViewpointImages([]);
        return;
      }

      const viewpointPlaceholders: ImageWithId[] = foundViewpoints.map((viewpoint) => ({
        id: viewpoint.id,
        name: viewpoint.name,
        viewpointId: viewpoint.id,
        src: '',
        isLoading: true,
        imageType: 'viewpoint',
      }));
      setViewpointImages(viewpointPlaceholders);

      const imageFetchPromises = foundViewpoints.map(async (viewpoint) => {
        const input = thumbnailInputFromLookAt(viewpoint, poiCenterPoint) as GenerateImageInput;
        if (!input) return { id: viewpoint.id, src: '', isLoading: false };

        try {
          const imageData = await fetchCropFromSpherical(input);
          const src = imageData ? `data:image/jpeg;base64,${imageData}` : '';
          return { id: viewpoint.id, src, isLoading: false };
        } catch {
          return { id: viewpoint.id, src: '', isLoading: false };
        }
      });

      const resolvedImages = await Promise.all(imageFetchPromises);

      setViewpointImages((prev) =>
        prev.map((img) => {
          const updatedViewpoints = resolvedImages.find((u) => u.id === img.id);
          return updatedViewpoints ? { ...img, ...updatedViewpoints } : img;
        })
      );
    };

    fetchViewpointImages();
  }, [foundViewpoints, poiCenterPoint, fetchCropFromSpherical]);

  const imagesWithIds = useMemo(() => {
    const imageList: ImageWithId[] = [];
    fileData.forEach((img) => {
      const isPrimary = img.imageType === ImageType.Primary;
      const imageType = isPrimary ? ImageType.Primary : ImageType.Other;
      imageList.push({
        src: getCloudfrontUrl(img.path),
        id: img.id!,
        uuid: img.uuid ?? undefined,
        name: img.name,
        imageType,
        fileName: img.fileName,
        caption: img.caption ?? undefined,
      });
    });

    const hasPrimary = imageList.find((image) => image.imageType === ImageType.Primary);
    if (!hasPrimary && currentPoi?.screenshotPaths?.viewpoint) {
      imageList.push({
        src: getCloudfrontUrl(currentPoi.screenshotPaths.viewpoint),
        id: 'viewpoint',
        name: currentPoi?.location?.name || 'Primary Image',
        imageType: ImageType.Primary,
        caption: undefined,
      });
    }
    const allImages = [...imageList, ...viewpointImages];
    return sortPoiAttachments(allImages, hasPrimary);
  }, [currentPoi.screenshotPaths, currentPoi?.location?.name, fileData, viewpointImages]);

  const currentImage = imagesWithIds[currentImageIndex];

  const handleImageNavigation = useCallback(
    (direction: NavigationDirection) => () => {
      const total = imagesWithIds.length;
      const newIndex =
        direction === NavigationDirection.Previous
          ? currentImageIndex > 0
            ? currentImageIndex - 1
            : total - 1
          : currentImageIndex < total - 1
            ? currentImageIndex + 1
            : 0;

      onChangeImage(newIndex);
      resetImageState();
      const newImage = imagesWithIds[newIndex];
      if (newImage && !newImage.isLoading && newImage.imageType !== 'viewpoint') {
        setMainImageLoading(true);
      }
    },
    [imagesWithIds, currentImageIndex, onChangeImage, resetImageState]
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const activeEl = document.activeElement as HTMLElement | null;
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        handleImageNavigation(NavigationDirection.Previous)();
      } else if (event.key === 'ArrowRight') {
        handleImageNavigation(NavigationDirection.Next)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleImageNavigation, imagesWithIds.length, currentImageIndex]);

  const handlePoiNavigation = (direction: NavigationDirection) => () => {
    setCurrentPoiIndex((previous) => {
      const total = allPois.length;
      const nextIndex =
        direction === NavigationDirection.Next
          ? (previous! + 1) % total
          : (previous! - 1 + total) % total;
      return nextIndex;
    });
    resetImageState();
    setMainImageLoading(true);
  };

  const handleThumbnailClick = (index: number) => {
    if (imagesWithIds[index].isLoading) return;

    onChangeImage(index);
    resetImageState();

    const targetImage = imagesWithIds[index];
    if (
      targetImage &&
      !targetImage.isLoading &&
      targetImage.imageType !== 'viewpoint' &&
      index !== currentImageIndex
    ) {
      setMainImageLoading(true);
    }
  };

  const handleImageLoad = () => {
    setMainImageLoading(false);
  };

  const handleImageError = () => {
    setMainImageLoading(false);
  };

  const selectedAttachment = useMemo(() => {
    return mapAttachmentType(currentImage);
  }, [currentImage]);
  if (!open) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {currentPoi && <DialogHeader currentPoi={currentPoi} onClose={onClose} />}

      <DialogContent sx={DialogContentStyle}>
        <MainContentContainer>
          <ImgNavButton
            id={`${LocationTag.ReviewPanelPoiModal}-previous-image-button`}
            onClick={handleImageNavigation(NavigationDirection.Previous)}
            disabled={imagesWithIds.length <= 1}
            sx={{ left: '28px' }}
          >
            <ChevronLeft sx={{ fontSize: '32px' }} />
          </ImgNavButton>

          <ImgNavButton
            id={`${LocationTag.ReviewPanelPoiModal}-next-image-button`}
            onClick={handleImageNavigation(NavigationDirection.Next)}
            disabled={imagesWithIds.length <= 1}
            sx={{ right: '1.5rem' }}
          >
            <ChevronRight sx={{ fontSize: '32px' }} />
          </ImgNavButton>

          <PoiNavigationContainer>
            <IconButton
              id={`${LocationTag.ReviewPanelPoiModal}-previous-poi-button`}
              onClick={handlePoiNavigation(NavigationDirection.Previous)}
              size="small"
              disabled={allPois.length <= 1}
            >
              <ChevronLeft sx={{ color: abyssColors.primary[700], fontSize: '32px' }} />
            </IconButton>
            <CurrentPoiName variant="body1">{poiName}</CurrentPoiName>
            <IconButton
              id={`${LocationTag.ReviewPanelPoiModal}-next-poi-button`}
              onClick={handlePoiNavigation(NavigationDirection.Next)}
              size="small"
              disabled={allPois.length <= 1}
            >
              <ChevronRight sx={{ color: abyssColors.primary[700], fontSize: '32px' }} />
            </IconButton>
          </PoiNavigationContainer>

          <MainImageSection
            currentImage={currentImage}
            mainImageLoading={mainImageLoading}
            imageTransform={imageTransform}
            imageEventHandlers={imageEventHandlers}
            handleImageLoad={handleImageLoad}
            handleImageError={handleImageError}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
            handleZoom={handleZoom}
          />
          <Box width="100%" display="flex" justifyContent="center" alignItems="center">
            {selectedAttachment && (
              <PointOfInterestCaption
                selectedAttachment={selectedAttachment}
                selectedPointOfInterest={currentPoi}
                location={LocationTag.ReviewPanelPoiModal}
              />
            )}
            <ImageNumbering sx={{ ml: 2 }}>
              {`${currentImageIndex + 1}/${imagesWithIds.length}`}
            </ImageNumbering>
          </Box>
          <ThumbnailList
            pointOfInterestId={currentPoi.id}
            imagesWithIds={imagesWithIds}
            currentIndex={currentImageIndex}
            handleThumbnailClick={handleThumbnailClick}
          />
        </MainContentContainer>
        {currentPoi && <DetailsSidebar data={currentPoi} />}
      </DialogContent>
    </StyledDialog>
  );
};
